import { useTranslation } from "react-i18next";
import { UploadCloud } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import type { VideoFormat } from "@/lib/hooks/useImageUpload";
import type { WatermarkSettings } from "../video-form-utils";

interface LogoControlsProps {
  format: VideoFormat;
  watermarkSettingsByFormat: Record<VideoFormat, WatermarkSettings>;
  setWatermarkSettingsByFormat: React.Dispatch<
    React.SetStateAction<Record<VideoFormat, WatermarkSettings>>
  >;
  isPlaying: boolean;
  watermarkPreview: string | null;
  handleWatermarkUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  activeTemplate: any;
  getContainerAlignment: (alignment: string) => React.CSSProperties;
}

export const LogoControls = ({
  format,
  watermarkSettingsByFormat,
  setWatermarkSettingsByFormat,
  isPlaying,
  watermarkPreview,
  handleWatermarkUpload,
  activeTemplate,
  getContainerAlignment,
}: LogoControlsProps) => {
  const { t } = useTranslation();

  const isLogoEnabled = watermarkSettingsByFormat[format].logoEnabled ?? true;

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
      {/* Toggle Active */}
      <div className="flex items-center justify-between p-4 bg-black/5 rounded-2xl border border-black/5 transition-all">
        <div className="space-y-0.5">
          <Label className="text-sm font-bold">{t("createVideo.logoActive") || "Show Logo"}</Label>
          <p className="text-[10px] text-muted-foreground">{t("createVideo.logoActiveHint") || "Toggle visibility of your brand logo"}</p>
        </div>
        <Checkbox 
          checked={isLogoEnabled}
          onCheckedChange={(checked) => {
            setWatermarkSettingsByFormat((prev) => ({
              ...prev,
              [format]: { ...prev[format], logoEnabled: !!checked },
            }));
          }}
          className="size-6 rounded-lg data-[state=checked]:bg-black data-[state=checked]:border-black"
        />
      </div>

      <div className={cn("space-y-8 transition-all duration-300", !isLogoEnabled && "opacity-40 grayscale pointer-events-none")}>
        {/* Logo Identity Card */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-bold">{t("createVideo.logoLabel") || "Brand Identity"}</Label>
            <label
              htmlFor="logo-upload-tab"
              className={cn(
                "text-[10px] font-bold text-muted-foreground hover:text-black cursor-pointer flex items-center gap-1.5 transition-all bg-black/5 px-2.5 py-1 rounded-lg",
                (isPlaying || !isLogoEnabled) && "opacity-50 cursor-not-allowed pointer-events-none"
              )}
            >
              <UploadCloud size={12} />
              {t("createVideo.changeLogo") || "Upload New"}
              <input
                id="logo-upload-tab"
                type="file"
                className="hidden"
                onChange={handleWatermarkUpload}
                accept="image/png, image/jpeg"
                disabled={isPlaying || !isLogoEnabled}
              />
            </label>
          </div>
          <div className="flex items-center gap-4 p-4 bg-white border border-black/5 rounded-2xl shadow-sm">
            <div className="w-12 h-12 rounded-xl bg-gray-50 border border-black/5 flex items-center justify-center overflow-hidden p-2 shadow-inner">
              <img src={watermarkPreview || ""} className="max-w-full max-h-full object-contain" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold truncate leading-tight">
                {activeTemplate?.name || "Default Branding"}
              </p>
              <p className="text-[10px] text-muted-foreground tracking-widest font-bold opacity-40 mt-0.5">
                Current Overlay
              </p>
            </div>
          </div>
        </div>

        {/* Opacity */}
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <Label className="text-sm font-bold 2k:text-xl 4k:text-3xl">
              {t("createVideo.opacity")}
            </Label>
            <span className="text-xs font-mono bg-black/5 px-2 py-0.5 rounded">
              {watermarkSettingsByFormat[format].opacity}%
            </span>
          </div>
          <Slider
            disabled={isPlaying || !isLogoEnabled}
            min={0}
            max={100}
            step={1}
            value={[watermarkSettingsByFormat[format].opacity]}
            onValueChange={(v) =>
              v[0] !== undefined &&
              setWatermarkSettingsByFormat((prev) => ({
                ...prev,
                [format]: { ...prev[format], opacity: v[0] },
              }))
            }
          />
        </div>

        {/* Padding */}
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <Label className="text-sm font-bold 2k:text-xl 4k:text-3xl">
              {t("createVideo.padding")}
            </Label>
            <span className="text-xs font-mono bg-black/5 px-2 py-0.5 rounded">
              {watermarkSettingsByFormat[format].padding}%
            </span>
          </div>
          <Slider
            disabled={isPlaying || !isLogoEnabled}
            min={0}
            max={25}
            step={1}
            value={[watermarkSettingsByFormat[format].padding]}
            onValueChange={(v) =>
              v[0] !== undefined &&
              setWatermarkSettingsByFormat((prev) => ({
                ...prev,
                [format]: { ...prev[format], padding: v[0] },
              }))
            }
          />
        </div>

        {/* Size */}
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <Label className="text-sm font-bold 2k:text-xl 4k:text-3xl">
              {t("createVideo.size")}
            </Label>
            <span className="text-xs font-mono bg-black/5 px-2 py-0.5 rounded">
              {watermarkSettingsByFormat[format].size}%
            </span>
          </div>
          <Slider
            disabled={isPlaying || !isLogoEnabled}
            min={1}
            max={100}
            step={1}
            value={[watermarkSettingsByFormat[format].size]}
            onValueChange={(v) =>
              v[0] !== undefined &&
              setWatermarkSettingsByFormat((prev) => ({
                ...prev,
                [format]: { ...prev[format], size: v[0] },
              }))
            }
          />
        </div>

        {/* Alignment */}
        <div className="space-y-6">
          <Label className="text-sm font-bold 2k:text-xl 4k:text-3xl">
            {t("createVideo.alignment")}
          </Label>
          <RadioGroup
            disabled={isPlaying || !isLogoEnabled}
            value={watermarkSettingsByFormat[format].alignment}
            onValueChange={(v) =>
              setWatermarkSettingsByFormat((prev) => ({
                ...prev,
                [format]: { ...prev[format], alignment: v },
              }))
            }
            className="grid grid-cols-3 gap-2"
          >
            {[
              "top-left", "top-center", "top-right",
              "center-left", "center", "center-right",
              "bottom-left", "bottom-center", "bottom-right",
            ].map((pos) => (
              <Label
                key={pos}
                htmlFor={`${format}-${pos}`}
                className={cn(
                  "flex items-center justify-center rounded-xl border border-black/5 bg-white hover:bg-black hover:text-white h-12 2k:h-20 4k:h-32 cursor-pointer transition-all",
                  watermarkSettingsByFormat[format].alignment === pos &&
                    "bg-black text-white shadow-lg",
                )}
              >
                <RadioGroupItem
                  value={pos}
                  id={`${format}-${pos}`}
                  className="sr-only"
                />
                <div
                  className="w-full h-full flex p-1.5"
                  style={getContainerAlignment(pos)}
                >
                  <div
                    className={cn(
                      "w-2 h-2 rounded-full",
                      watermarkSettingsByFormat[format].alignment === pos
                        ? "bg-white"
                        : "bg-black",
                    )}
                  />
                </div>
              </Label>
            ))}
          </RadioGroup>
        </div>
      </div>
    </div>
  );
};
